import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "flight-details" }
const _hoisted_2 = { class: "row align-items-center" }
const _hoisted_3 = { class: "col-8" }
const _hoisted_4 = {
  key: 0,
  class: "mb-1"
}
const _hoisted_5 = { class: "mb-0 caption" }
const _hoisted_6 = { class: "caption-strong" }
const _hoisted_7 = { class: "caption-strong" }
const _hoisted_8 = { class: "caption-strong" }
const _hoisted_9 = {
  key: 1,
  class: "mb-1"
}
const _hoisted_10 = { class: "mb-0 caption" }
const _hoisted_11 = { class: "caption-strong" }
const _hoisted_12 = { class: "caption-strong" }
const _hoisted_13 = { class: "caption-strong" }
const _hoisted_14 = { class: "mb-0 caption" }
const _hoisted_15 = { class: "col-4 d-flex align-items-start justify-content-center" }
const _hoisted_16 = { class: "airline-logos-container" }
const _hoisted_17 = ["src", "alt"]
const _hoisted_18 = ["src", "alt"]
const _hoisted_19 = ["src", "alt"]

import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { getAirportName } from '@/utils/locationCodes'

  // Initialize i18n
  
export default /*@__PURE__*/_defineComponent({
  __name: 'FlightDetails',
  props: {
    tripInformation: {}
  },
  setup(__props: any) {

  const { t } = useI18n()

  // Helper function to get airline icon URL
  const getAirlineIconUrl = (airlineCode: string): string => {
    if (!airlineCode) return ''
    
    const normalizedCode = airlineCode.trim().toUpperCase()
    
    const imageMap: Record<string, () => string> = {
      BA: () => require('@/assets/carrier-logos/BA.png'),
      AF: () => require('@/assets/carrier-logos/AF.png'),
      FR: () => require('@/assets/carrier-logos/FR.png'),
      LH: () => require('@/assets/carrier-logos/LH.png'),
      LX: () => require('@/assets/carrier-logos/LX.png'),
      KL: () => require('@/assets/carrier-logos/KL.png'),
      LS: () => require('@/assets/carrier-logos/LS.png'),
      U2: () => require('@/assets/carrier-logos/U2.png'),
      W9: () => require('@/assets/carrier-logos/W9.png'),
      BY: () => require('@/assets/carrier-logos/BY.png'),
    }
    
    return imageMap[normalizedCode]?.() || ''
  }

  // Helper function to get the primary airline code (prefer outbound, fallback to inbound)
  const getPrimaryAirlineCode = (): string => {
    if (outboundFlight.value?.airlineCode) {
      return outboundFlight.value.airlineCode
    }
    if (inboundFlight.value?.airlineCode) {
      return inboundFlight.value.airlineCode
    }
    return ''
  }

  // Check if we should show both logos (different companies for outbound and inbound)
  const showBothLogos = computed(() => {
    return outboundFlight.value?.airlineCode && 
           inboundFlight.value?.airlineCode && 
           outboundFlight.value.airlineCode !== inboundFlight.value.airlineCode
  })

  // Define the props
  const props = __props

  // Get flight details object
  const flightDetail = computed(() => {
    if (Array.isArray(props.tripInformation) && props.tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      return props.tripInformation[0].details || props.tripInformation[0].tripInformation
    }
    return props.tripInformation
  })

  // Get outbound flight information
  const outboundFlight = computed(() => {
    const flight = flightDetail.value
    if (flight?.outboundTrip) {
      const departureAirport = getAirportName(flight.outboundTrip.departure?.location || '')
      const arrivalAirport = getAirportName(flight.outboundTrip.arrival?.location || '')
      const departureTime = flight.outboundTrip.departure?.time || ''
      const arrivalTime = flight.outboundTrip.arrival?.time || ''
      const airlineCode = flight.outboundTrip.company || ''
      return {
        departureAirport,
        arrivalAirport,
        departureTime,
        arrivalTime,
        airlineCode
      }
    }
    return null
  })

  // Get inbound flight information
  const inboundFlight = computed(() => {
    const flight = flightDetail.value
    if (flight?.inboundTrip) {
      const departureAirport = getAirportName(flight.inboundTrip.departure?.location || '')
      const arrivalAirport = getAirportName(flight.inboundTrip.arrival?.location || '')
      const departureTime = flight.inboundTrip.departure?.time || ''
      const arrivalTime = flight.inboundTrip.arrival?.time || ''
      const airlineCode = flight.inboundTrip.company || ''
      return {
        departureAirport,
        arrivalAirport,
        departureTime,
        arrivalTime,
        airlineCode
      }
    }
    return null
  })

  // Handle image loading errors
  const handleImageError = (event: Event) => {
    const img = event.target as HTMLImageElement
    img.style.display = 'none' // Hide the image if it fails to load
  }

  // Helper function to check if flight details are valid
  const hasFlightDetails = () => {
    const { tripInformation } = props
    // Handle array of tripInformation (most common case)
    if (Array.isArray(tripInformation) && tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      const flightDetail = tripInformation[0].details || tripInformation[0].tripInformation
      return flightDetail && (flightDetail.outboundTrip || flightDetail.inboundTrip || flightDetail.class)
    }
    // Handle direct object (fallback)
    return tripInformation && (tripInformation.outboundTrip || tripInformation.inboundTrip || tripInformation.class)
  }

  // Helper function to get flight class
  const getFlightClass = () => {
    const flight = flightDetail.value
    
    if (flight?.class) {
      return flight.class
    } else if (flight?.outboundTrip?.class) {
      return flight.outboundTrip.class
    } else if (flight?.inboundTrip?.class) {
      return flight.inboundTrip.class
    }
    return 'Economy'
  }



return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (outboundFlight.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("p", _hoisted_5, [
                _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(t)('reservations.outbound')), 1),
                _createTextVNode(" " + _toDisplayString(outboundFlight.value.departureAirport) + " ", 1),
                _createElementVNode("span", _hoisted_7, _toDisplayString(outboundFlight.value.departureTime), 1),
                _createTextVNode(" → " + _toDisplayString(outboundFlight.value.arrivalAirport) + " ", 1),
                _createElementVNode("span", _hoisted_8, _toDisplayString(outboundFlight.value.arrivalTime), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        (inboundFlight.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createElementVNode("p", _hoisted_10, [
                _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(t)('reservations.return')), 1),
                _createTextVNode(" " + _toDisplayString(inboundFlight.value.departureAirport) + " ", 1),
                _createElementVNode("span", _hoisted_12, _toDisplayString(inboundFlight.value.departureTime), 1),
                _createTextVNode(" → " + _toDisplayString(inboundFlight.value.arrivalAirport) + " ", 1),
                _createElementVNode("span", _hoisted_13, _toDisplayString(inboundFlight.value.arrivalTime), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("p", _hoisted_14, _toDisplayString(getFlightClass()) + " " + _toDisplayString(_unref(t)('reservations.class')), 1)
      ]),
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("div", _hoisted_16, [
          (showBothLogos.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (outboundFlight.value?.airlineCode)
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      src: getAirlineIconUrl(outboundFlight.value.airlineCode),
                      alt: `${outboundFlight.value.airlineCode} logo`,
                      class: "airline-logo mb-1",
                      onError: handleImageError
                    }, null, 40, _hoisted_17))
                  : _createCommentVNode("", true),
                (inboundFlight.value?.airlineCode)
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 1,
                      src: getAirlineIconUrl(inboundFlight.value.airlineCode),
                      alt: `${inboundFlight.value.airlineCode} logo`,
                      class: "airline-logo",
                      onError: handleImageError
                    }, null, 40, _hoisted_18))
                  : _createCommentVNode("", true)
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (getPrimaryAirlineCode())
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      src: getAirlineIconUrl(getPrimaryAirlineCode()),
                      alt: `${getPrimaryAirlineCode()} logo`,
                      class: "airline-logo",
                      onError: handleImageError
                    }, null, 40, _hoisted_19))
                  : _createCommentVNode("", true)
              ], 64))
        ])
      ])
    ])
  ]))
}
}

})